import React, { Fragment, useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import { useDebouncedCallback } from "use-debounce";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import ReactModal from "react-modal";
import NumberFormat from "react-number-format";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import getApiErrorMessage from "../../helpers/get-api-error-message";

import axios from "axios";
import ai from "../../helpers/axios";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Loader from "../loader";
import Search from "../search";
import { columns } from "./featured-one-way-columns";
import { LoadingButton } from "../shared/loading-button";
import TextInput from "../text-input";

momentTimezone.moment = moment;

const initialState = {
  isLoading: true,
  isSaving: false,
  isImporting: false,
  data: [],
  totalLength: 0,
  nextSheetId: 1,
  query: "",
  countPerPage: 25,
  currentPage: 1,
  sortBy: "date",
  sortDirection: "asc",
  selectedRows: [],
  clearSelectedRows: false,
  showEditModal: false,
  selectedTrip: null,
  showDeleteModal: false,
  idsToDelete: [],
  refreshDataToken: Date.now(),
  showInvalidImportFileModal: false,
  showInvalidImportValuesModal: false,
  showDuplicateImportValuesModal: false,
  importErrors: [],
  importDuplicates: [],
  importFile: null,
};

const BOOLEAN_OPTIONS = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const AIRCRAFT_TYPE_OPTIONS = [
  {
    value: "turboprop_instant",
    label: "Turboprop",
    aircraft: "Turboprop",
    aircraftTypeId: "5fef7df7507f6f13ce3e5658",
    seats: 6,
    example: "Pilatus PC-12 and similar",
  },
  {
    value: "light_jet_instant",
    label: "Light Jet",
    aircraft: "Light Jet",
    aircraftTypeId: "5fef7df7507f6f13ce3e565f",
    seats: 6,
    example: "Phenom 300 and similar",
  },
  {
    value: "midsize_jet_instant",
    label: "Midsize Jet",
    aircraft: "Midsize Jet",
    aircraftTypeId: "5fef7df7507f6f13ce3e565b",
    seats: 7,
    example: "Lear 60 and similar",
  },
  {
    value: "super_midsize_jet_instant",
    label: "Super Midsize Jet",
    aircraft: "Super Midsize Jet",
    aircraftTypeId: "5fef7df7507f6f13ce3e565a",
    seats: 8,
    example: "Challenger 300 and similar",
  },
  {
    value: "heavy_10_instant",
    label: "Heavy Jet (10)",
    aircraft: "Heavy Jet",
    aircraftTypeId: "5fef7df7507f6f13ce3e5659",
    seats: 10,
    example: "Challenger 604 and similar",
  },
  {
    value: "heavy_12_instant",
    label: "Heavy Jet (12)",
    aircraft: "Heavy Jet",
    aircraftTypeId: "5fef7df7507f6f13ce3e5659",
    seats: 12,
    example: "Gulfstream G-450 and similar",
  },
  {
    value: "heavy_14_instant",
    label: "Heavy Jet (14)",
    aircraft: "Heavy Jet",
    aircraftTypeId: "5fef7df7507f6f13ce3e5659",
    seats: 14,
    example: "Global 5000 and similar",
  },
];

const PRICE_STRENGTH_OPTIONS = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
];

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        isLoading: true,
        selectedRows: [],
        clearSelectedRows: !state.clearSelectedRows,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        isLoading: false,
        data: action?.payload?.data?.data || [],
        totalLength: action?.payload?.data?.countTotal,
        nextSheetId: action?.payload?.data?.nextSheetId,
      };
    }

    case "search": {
      return {
        ...state,
        query: action.query,
        currentPage: 1,
      };
    }

    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }

    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }

    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
        currentPage: 1,
      };
    }

    case "rowSelection": {
      return {
        ...state,
        selectedRows: action.payload.selectedRows,
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "openEditModal": {
      return {
        ...state,
        showEditModal: true,
        selectedTrip: action.item || null,
      };
    }

    case "openDeleteModal": {
      return {
        ...state,
        showDeleteModal: true,
        idsToDelete: action.idsToDelete,
      };
    }

    case "closeModal": {
      return {
        ...state,
        showEditModal: false,
        showDeleteModal: false,
        showInvalidImportFileModal: false,
        showInvalidImportValuesModal: false,
        showDuplicateImportValuesModal: false,
        selectedTrip: null,
        idsToDelete: [],
        importErrors: [],
        importDuplicates: [],
        importFile: null,
      };
    }

    case "selectedTripFieldChanged": {
      return {
        ...state,
        selectedTrip: {
          ...(state.selectedTrip || {}),
          [action.field]: action.value,
        },
      };
    }

    case "selectedTripFowAircraftMappingFieldChanged": {
      return {
        ...state,
        selectedTrip: {
          ...(state.selectedTrip || {}),
          fowAircraftMapping: {
            ...(state.selectedTrip?.fowAircraftMapping || {}),
            [action.field]: action.value,
          },
        },
      };
    }

    default:
      break;
  }
  return state;
};

const FeaturedOneWayContent = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef(null);

  const inputRef = useRef(null);

  useEffect(() => {
    document.title = "Featured One Way | evoJets HQ";
  }, []);

  const getData = useCallback(async (params) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    dispatch({
      type: "dataBeganDownloading",
    });

    try {
      const response = await ai
        .auth(token)
        .get(`/api/featuredOneWays`, {
          params,
          signal,
        });

      if (response?.data) {
        dispatch({
          type: "dataDownloaded",
          payload: {
            data: response.data,
          },
        });
      }
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error?.response?.data);
      Sentry.captureException(error);
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const debouncedGetData = useDebouncedCallback(
    (params) => {
      getData(params);
    },
    500,
  );

  const handlePageChange = (page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const handleSort = (column, _) => {
    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection: newDirection,
      },
    });
  };

  const handleSearchInputChange = (e) => {
    let query = e.target.value.toLowerCase();
    dispatch({ type: "search", query });
  };

  useEffect(() => {
    debouncedGetData({
      query: state.query,
      page: state.currentPage,
      countPerPage: state.countPerPage,
      sortBy: state.sortBy,
      sortDirection: state.sortDirection,
    });
  }, [
    debouncedGetData,
    state.query,
    state.currentPage,
    state.countPerPage,
    state.sortBy,
    state.sortDirection,
    state.refreshDataToken,
  ]);

  const handleOpenImportFileSelect = useCallback((event) => {
    event.preventDefault();

    if (inputRef.current) {
      inputRef.current.click();
    }
  }, []);

  const handleImport = useCallback(async (event, force = false, overwriteDuplicates = false) => {
    const fileUploaded = event.target.files[0];
    event.target.value = null;

    if (!fileUploaded || fileUploaded.type !== "text/csv") {
      dispatch({
        type: "fieldChanged",
        field: "showInvalidImportFileModal",
        value: true,
      });
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isImporting",
      value: true,
    });

    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    const formData = new FormData();
    formData.append("attachments[]", fileUploaded);

    if (force) {
      formData.append("force", force);
    }
    if (overwriteDuplicates) {
      formData.append("overwriteDuplicates", overwriteDuplicates);
    }

    let requestFailed = false;
    const response = await ai
      .auth(token)
      .post("/api/featuredOneWays/import", formData)
      .catch((error) => {
        requestFailed = true;
        toast.error(getApiErrorMessage(error, "Import failed."));
      });

    dispatch({
      type: "fieldChanged",
      field: "isImporting",
      value: false,
    });

    if (requestFailed) {
      return;
    }

    if (!response?.data) {
      dispatch({
        type: "fieldChanged",
        field: "showInvalidImportFileModal",
        value: true,
      });
      return;
    }
    const { data } = response;

    if (!data.success) {
      if (!!data.duplicates?.length) {
        dispatch({
          type: "fieldChanged",
          field: "showDuplicateImportValuesModal",
          value: true,
        });
        dispatch({
          type: "fieldChanged",
          field: "importDuplicates",
          value: data.duplicates,
        });
      } else {
        dispatch({
          type: "fieldChanged",
          field: "showInvalidImportValuesModal",
          value: true,
        });
        dispatch({
          type: "fieldChanged",
          field: "importErrors",
          value: data.errors,
        });
      }
      dispatch({
        type: "fieldChanged",
        field: "importFile",
        value: fileUploaded,
      });
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "refreshDataToken",
      value: Date.now(),
    });
    toast.success("Import is completed");
  }, [getAccessTokenSilently, loginWithRedirect]);

  const handleImportForce = useCallback(() => {
    dispatch({
      type: "closeModal",
    });

    handleImport({
      target: {
        files: [state.importFile],
      },
    }, true);
  }, [handleImport, state.importFile]);

  const handleImportWithOverwrite = useCallback(() => {
    dispatch({
      type: "closeModal",
    });

    handleImport({
      target: {
        files: [state.importFile],
      },
    }, true, true);
  }, [handleImport, state.importFile]);

  const handleAdd = useCallback((event) => {
    event.preventDefault();

    dispatch({
      type: "openEditModal",
      item: {
        sheetId: state.nextSheetId,
      },
    });
  }, [state.nextSheetId]);

  const handleEdit = useCallback((event, row) => {
    event.preventDefault();

    dispatch({
      type: "openEditModal",
      item: row,
    });
  }, []);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (
      !state.selectedTrip ||
      !state.selectedTrip.sheetId ||
      !state.selectedTrip.date ||
      state.selectedTrip.defaultDeparture === undefined ||
      !state.selectedTrip.departureAirport?._id ||
      !state.selectedTrip.arrivalAirport?._id ||
      !state.selectedTrip.fowAircraftMapping?.internalType ||
      !state.selectedTrip.seats ||
      !state.selectedTrip.price ||
      state.selectedTrip.ccEligible === undefined ||
      !state.selectedTrip.ccFee ||
      !state.selectedTrip.ccPrice ||
      !state.selectedTrip.priceStrength
    ) {
      return toast.error(
        "All fields are required",
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);

      return loginWithRedirect();
    });

    const date = state.selectedTrip.defaultDeparture === true ? moment(state.selectedTrip.date).hours(0).minutes(0).toISOString() : state.selectedTrip.date;

    const data = {
      sheetId: state.selectedTrip.sheetId,
      date,
      defaultDeparture: state.selectedTrip.defaultDeparture,
      departureAirportId: state.selectedTrip.departureAirport?._id,
      departCode: state.selectedTrip.departureAirport?.code,
      departDisplayName: state.selectedTrip.departureAirport?.fowDisplayName || state.selectedTrip.departureAirport?.code,
      departureRegions: state.selectedTrip.departureAirport?.fowRegions || [],
      arrivalAirportId: state.selectedTrip.arrivalAirport?._id,
      arriveCode: state.selectedTrip.arrivalAirport?.code,
      arriveDisplayName: state.selectedTrip.arrivalAirport?.fowDisplayName || state.selectedTrip.arrivalAirport?.code,
      arrivalRegions: state.selectedTrip.arrivalAirport?.fowRegions || [],
      fowAircraftMapping: state.selectedTrip.fowAircraftMapping,
      aircraft: state.selectedTrip.aircraft,
      aircraftTypeId: state.selectedTrip.aircraftTypeId,
      seats: state.selectedTrip.seats,
      price: +state.selectedTrip.price,
      ccEligible: state.selectedTrip.ccEligible,
      ccFee: +state.selectedTrip.ccFee,
      ccPrice: +state.selectedTrip.ccPrice,
      priceStrength: state.selectedTrip.priceStrength,
      expDateTime: moment(date).subtract(10, "days").hours(17).toISOString(),
    };
    let response;
    if (state.selectedTrip._id) {
      response = await ai
        .auth(token)
        .put(`/api/featuredOneWays/${state.selectedTrip._id}`, data)
        .catch((error) => {
          toast.error(
            error.response.data,
          );
          Sentry.captureException(error);
        });
    } else {
      response = await ai
        .auth(token)
        .post(`/api/featuredOneWays`, data)
        .catch((error) => {
          toast.error(
            error.response.data,
          );
          Sentry.captureException(error);
        });

    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: false,
    });

    if (response?.data) {
      dispatch({
        type: "fieldChanged",
        field: "refreshDataToken",
        value: Date.now(),
      });

      dispatch({
        type: "closeModal",
      });
    }
  }, [state.selectedTrip, getAccessTokenSilently, loginWithRedirect]);

  const handleDelete = useCallback((event, row) => {
    event.preventDefault();

    dispatch({
      type: "openDeleteModal",
      idsToDelete: [row._id],
    });
  }, []);

  const handleDeleteSelected = useCallback((event) => {
    event.preventDefault();

    if (!state.selectedRows) {
      return;
    }

    dispatch({
      type: "openDeleteModal",
      idsToDelete: state.selectedRows.map(({ _id }) => _id),
    });
  }, [state.selectedRows]);

  const handleCloseModal = useCallback(() => {
    dispatch({
      type: "closeModal",
    });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!state.idsToDelete.length) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const response = await ai
      .auth(token)
      .delete(`/api/featuredOneWays`, {
        data: { ids: state.idsToDelete },
      })
      .catch((error) => {
        console.error(error.response?.data);
        Sentry.captureException(error);
      });

    if (response?.data) {
      dispatch({
        type: "fieldChanged",
        field: "refreshDataToken",
        value: Date.now(),
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: false,
    });

    dispatch({
      type: "closeModal",
    });
  }, [getAccessTokenSilently, loginWithRedirect, state.idsToDelete]);

  const columnsConfig = useMemo(() => ([
    ...columns,
    {
      name: "Actions",
      selector: "actions",
      sortable: false,
      width: "90px",
      cell: (row) => {
        return (
          <div className="d-flex justify-content-end">
            <button
              className="base-button icon-button icon-button-small evo-blue"
              title="Edit Featured One Way trip"
              onClick={(event) => handleEdit(event, row)}
            >
              <span className="material-icons">edit</span>
            </button>

            <button
              className="base-button icon-button icon-button-small btn-danger ml-2"
              title="Delete Featured One Way trip"
              onClick={(event) => handleDelete(event, row)}
            >
              <span className="material-icons">delete</span>
            </button>
          </div>
        );
      },
    },
  ]), [handleEdit, handleDelete]);

  const handleSelection = function (rowData) {
    dispatch({
      type: "rowSelection",
      payload: {
        selectedRows: rowData.selectedRows,
        allSelected: rowData.allSelected,
      },
    });
  };

  const loadAirportOptions = useCallback(async (inputValue) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);

      return loginWithRedirect();
    });

    const { data } = await ai
      .auth(token)
      .get(`/api/airports/search?q=${inputValue}&includeTZ=true`);

    return data?.map((airport) => ({
      value: airport._id,
      label: airport.airportName,
      code: airport.code,
      fowRegions: airport.fowRegions,
      fowDisplayName: airport.fowDisplayName,
    }));
  }, [getAccessTokenSilently, loginWithRedirect]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1="Featured One Way trips"
        pretitle="Business"
        breadcrumbs={[{ link: "/featured-one-ways", label: "Featured One Ways" }]}
        extraClasses="page-header-two-columns-l1"
        rightColumn={
          <div className="page-header-two-column-actions-row">
            <Search
              onSearch={handleSearchInputChange}
              placeholder="Search"
              query={state.query}
            />

            <button
              type="button"
              className="base-button evo-blue"
              title="Add Featured One Way trip"
              onClick={handleAdd}
            >
              Add
            </button>

            <LoadingButton
              type="button"
              className="base-button evo-blue"
              title="Import Featured One Way trips"
              onClick={handleOpenImportFileSelect}
              isLoading={state.isImporting && !state.showInvalidImportValuesModal}
            >
              Import
            </LoadingButton>

            <input className="hidden" ref={inputRef} type="file" accept=".csv" onChange={handleImport}/>

            <button
              type="button"
              className="base-button btn-danger"
              disabled={!state.selectedRows?.length}
              onClick={handleDeleteSelected}
            >
              Delete Selected
            </button>
          </div>
        }
      />

      <div className="server-pagination-table-container trips-list-table-container">
        <StickyHeaderDataTable
          columns={columnsConfig}
          data={state.data}
          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[25, 50, 100, 150, 200, 250, 500]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={state.sortBy}
          sortDirection={state.sortDirection}
          progressPending={state.isLoading}
          progressComponent={<Loader/>}
          persistTableHead
          highlightOnHover
          responsive
          onSelectedRowsChange={handleSelection}
          selectableRows
          clearSelectedRows={state.clearSelectedRows}
        />
      </div>

      <ReactModal
        isOpen={state.showEditModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "700px",
            maxHeight: "80vh",
            minWidth: "calc(min(700px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header">
          <h4>{state.selectedTrip?._id ? "Edit" : "Add"} Featured One Way Trip</h4>
        </div>

        <div className="modal-body width-100 modal-body-with-scroll">
          <form className="w-100 base-form mt-10">
            <div className="w-100 d-flex gap-20">
              <TextInput
                containerClasses="flex-half"
                name="sheetId"
                label="Sheet ID"
                required
                handleChange={(event) => {
                  dispatch({
                    type: "selectedTripFieldChanged",
                    field: "sheetId",
                    value: event.target.value,
                  });
                }}
                value={state.selectedTrip?.sheetId}
              />

              <div className="datepickerContainer mb-30 flex-half">
                <label htmlFor="date" className="base-label">
                  Date <span className="required">*</span>
                </label>

                <Datepicker
                  value={state.selectedTrip?.date}
                  onChange={(event) => {
                    dispatch({
                      type: "selectedTripFieldChanged",
                      field: "date",
                      value: event.value?.toISOString() || null,
                    });
                  }}
                  dataTimezone="utc"
                  displayTimezone="local"
                  timezonePlugin={momentTimezone}
                  controls={["date"]}
                />
              </div>
            </div>

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-half">
                <label htmlFor="defaultDeparture" className="base-label">
                  Default Departure <span className="required">*</span>
                </label>

                <Select
                  className="base-select"
                  classNamePrefix="select"
                  name="defaultDeparture"
                  options={BOOLEAN_OPTIONS}
                  placeholder="Select default departure"
                  value={BOOLEAN_OPTIONS.find(({ value }) => value === state.selectedTrip?.defaultDeparture) || null}
                  onChange={(selectedValue) => {
                    dispatch({
                      type: "selectedTripFieldChanged",
                      field: "defaultDeparture",
                      value: selectedValue.value,
                    });
                    return selectedValue.value;
                  }}
                />
              </div>

              <div className="form-group flex-half">
                {state.selectedTrip?.defaultDeparture === false &&
                  (
                    <>
                      <label htmlFor="time">Time</label> <span className="required">*</span>

                      <Datepicker
                        onChange={(event) => {
                          dispatch({
                            type: "selectedTripFieldChanged",
                            field: "date",
                            value: event.value,
                          });
                        }}
                        timezonePlugin={momentTimezone}
                        dataTimezone="utc"
                        displayTimezone="local"
                        controls={["time"]}
                        value={state.selectedTrip?.date}
                        defaultSelection="12:00 am"
                      /></>
                  )}
              </div>
            </div>

            <div className="w-100 d-flex gap-20">
              <div className="airportContainer form-group flex-half">
                <label
                  htmlFor="departureAirport"
                  className="base-label"
                >
                  Departure <span className="required">*</span>
                </label>

                <AsyncSelect
                  name="departureAirport"
                  className="departure-airport-select mb-0"
                  classNamePrefix="select"
                  cacheOptions
                  loadOptions={loadAirportOptions}
                  placeholder="Search Airports"
                  defaultValue={{
                    label: state.selectedTrip?.departureAirport?.airportName,
                    value: state.selectedTrip?.departureAirport?._id,
                  }}
                  onChange={(selectedAirport) => {
                    dispatch({
                      type: "selectedTripFieldChanged",
                      field: "departureAirport",
                      value: {
                        _id: selectedAirport?.value || "",
                        airportName: selectedAirport?.label || "",
                        code: selectedAirport?.code || "",
                        fowRegions: selectedAirport?.fowRegions || "",
                        fowDisplayName: selectedAirport?.fowDisplayName || "",
                      },
                    });
                  }}
                  menuPosition="fixed"
                />
              </div>

              <div className="airportContainer form-group flex-half">
                <label
                  htmlFor="arrivalAirport"
                  className="base-label"
                >
                  Arrival <span className="required">*</span>
                </label>

                <AsyncSelect
                  name="arrivalAirport"
                  className="arrival-airport-select mb-0"
                  classNamePrefix="select"
                  cacheOptions
                  loadOptions={loadAirportOptions}
                  placeholder="Search Airports"
                  defaultValue={{
                    label: state.selectedTrip?.arrivalAirport?.airportName,
                    value: state.selectedTrip?.arrivalAirport?._id,
                  }}
                  onChange={(selectedAirport) => {
                    dispatch({
                      type: "selectedTripFieldChanged",
                      field: "arrivalAirport",
                      value: {
                        _id: selectedAirport?.value || "",
                        airportName: selectedAirport?.label || "",
                        code: selectedAirport?.code || "",
                        fowRegions: selectedAirport?.fowRegions || "",
                        fowDisplayName: selectedAirport?.fowDisplayName || "",
                      },
                    });
                  }}
                  menuPosition="fixed"
                />
              </div>
            </div>

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-half">
                <label htmlFor="aircraftInternalType" className="base-label">
                  Aircraft <span className="required">*</span>
                </label>

                <Select
                  className="base-select"
                  classNamePrefix="select"
                  name="aircraftInternalType"
                  options={AIRCRAFT_TYPE_OPTIONS}
                  placeholder="Select aircraft"
                  value={AIRCRAFT_TYPE_OPTIONS.find(({ value }) => value === state.selectedTrip?.fowAircraftMapping?.internalType) || null}
                  onChange={(selectedValue) => {
                    if (!selectedValue) {
                      return;
                    }

                    dispatch({
                      type: "selectedTripFowAircraftMappingFieldChanged",
                      field: "internalType",
                      value: selectedValue.value,
                    });
                    dispatch({
                      type: "selectedTripFowAircraftMappingFieldChanged",
                      field: "seats",
                      value: selectedValue.seats,
                    });
                    dispatch({
                      type: "selectedTripFowAircraftMappingFieldChanged",
                      field: "exampleAircraft",
                      value: selectedValue.example,
                    });
                    dispatch({
                      type: "selectedTripFieldChanged",
                      field: "seats",
                      value: selectedValue.seats,
                    });
                    dispatch({
                      type: "selectedTripFieldChanged",
                      field: "aircraft",
                      value: selectedValue.aircraft,
                    });
                    dispatch({
                      type: "selectedTripFieldChanged",
                      field: "aircraftTypeId",
                      value: selectedValue.aircraftTypeId,
                    });

                    return selectedValue.value;
                  }}
                  menuPosition="fixed"
                />
              </div>

              <TextInput
                containerClasses="flex-half"
                name="seats"
                label="Seats"
                disabled={!state.selectedTrip?.fowAircraftMapping?.internalType}
                handleChange={(event) => {
                  dispatch({
                    type: "selectedTripFieldChanged",
                    field: "seats",
                    value: parseInt(event.target.value) || 0,
                  });
                }}
                value={state.selectedTrip?.seats}
                type="number"
                className="base-input"
                required
              />
            </div>

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-half">
                <label>Price <span className="required">*</span></label>

                <NumberFormat
                  value={state.selectedTrip?.price}
                  thousandSeparator
                  allowNegative={false}
                  prefix="$"
                  onValueChange={(values) => {
                    dispatch({
                      type: "selectedTripFieldChanged",
                      field: "price",
                      value: values.value,
                    });
                  }}
                  placeholder="Price"
                  className="semibold-text"
                  decimalScale={2}
                  name="price"
                />
              </div>

              <div className="form-group flex-half">
                <label htmlFor="ccEligible" className="base-label">
                  CC Eligible <span className="required">*</span>
                </label>

                <Select
                  className="base-select"
                  classNamePrefix="select"
                  name="ccEligible"
                  options={BOOLEAN_OPTIONS}
                  placeholder="Select CC Eligible"
                  value={BOOLEAN_OPTIONS.find(({ value }) => value === state.selectedTrip?.ccEligible) || null}
                  onChange={(selectedValue) => {
                    dispatch({
                      type: "selectedTripFieldChanged",
                      field: "ccEligible",
                      value: selectedValue.value,
                    });
                    return selectedValue.value;
                  }}
                />
              </div>
            </div>

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-half">
                <label>CC Price <span className="required">*</span></label>

                <NumberFormat
                  value={state.selectedTrip?.ccPrice}
                  thousandSeparator
                  allowNegative={false}
                  prefix="$"
                  onValueChange={(values) => {
                    dispatch({
                      type: "selectedTripFieldChanged",
                      field: "ccPrice",
                      value: values.value,
                    });
                  }}
                  placeholder="CC Price"
                  className="semibold-text"
                  decimalScale={2}
                  name="ccPrice"
                />
              </div>

              <div className="form-group flex-half">
                <label>CC Fee <span className="required">*</span></label>

                <NumberFormat
                  value={state.selectedTrip?.ccFee}
                  thousandSeparator
                  allowNegative={false}
                  prefix="$"
                  onValueChange={(values) => {
                    dispatch({
                      type: "selectedTripFieldChanged",
                      field: "ccFee",
                      value: values.value,
                    });
                  }}
                  placeholder="CC Fee"
                  className="semibold-text"
                  decimalScale={2}
                  name="ccFee"
                />
              </div>
            </div>

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-half">
                <label htmlFor="priceStrength" className="base-label">
                  Price Strength <span className="required">*</span>
                </label>

                <Select
                  className="base-select"
                  classNamePrefix="select"
                  name="priceStrength"
                  options={PRICE_STRENGTH_OPTIONS}
                  placeholder="Select Price Strengt"
                  value={PRICE_STRENGTH_OPTIONS.find(({ value }) => value === state.selectedTrip?.priceStrength) || null}
                  onChange={(selectedValue) => {
                    dispatch({
                      type: "selectedTripFieldChanged",
                      field: "priceStrength",
                      value: selectedValue.value,
                    });
                    return selectedValue.value;
                  }}
                  menuPosition="fixed"
                />
              </div>
              <div className="form-group flex-half"/>
            </div>
          </form>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <LoadingButton
            className="base-button evo-blue flex-half"
            type="button"
            onClick={handleSubmit}
            isLoading={state.isSaving}
          >
            Submit
          </LoadingButton>

          <button
            className="base-button btn-danger flex-half"
            type="button"
            onClick={handleCloseModal}
            disabled={state.isSaving}
          >
            Cancel
          </button>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDeleteModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete Featured One Way Trip{state.idsToDelete.length > 1 && "s"}</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            Are you sure you want to delete {state.idsToDelete.length > 1 ? "selected records" : "this record"}? This
            action cannot be undone.
          </div>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleCloseModal}
              disabled={state.isSaving}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              onClick={handleDeleteConfirm}
              isLoading={state.isSaving}
            >
              Delete
            </LoadingButton>
          </div>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showInvalidImportFileModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Invalid import file</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            Selected file is not valid for Featured One Way import.
          </div>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex">
            <button
              className="base-button evo-blue flex-grow-1"
              type="button"
              onClick={handleCloseModal}
            >
              Close
            </button>
          </div>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showInvalidImportValuesModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Invalid values</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            Some values in the provided import file are not valid. Yo can choose to proceed with import and only valid
            FOW records are going to be imported or you can cancel import and correct invalid values.
          </div>
          <div className="w-100 mt-20">
            List of invalid values (per row):
            <ul>
              {state.importErrors.map(({ index, fields }) => (
                <li key={index}>Row {+index + 1}: {fields.join(", ")}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleCloseModal}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              isLoading={state.isImporting}
              onClick={handleImportForce}
            >
              Import
            </LoadingButton>
          </div>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDuplicateImportValuesModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Duplicate sheetId(s)</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            Some rows in the provided import file use sheetId values that already exist in the system.
            <br/>
            You can choose to proceed with the import, in which case those rows will overwrite the existing FOW records
            with the same sheetId in the database.
          </div>
          <div className="w-100 mt-20">
            Rows with duplicate sheetID:
            <ul>
              {state.importDuplicates.map(({ index, sheetId }) => (
                <li key={index}>Row {+index + 1}: sheetID = {sheetId}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleCloseModal}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              isLoading={state.isImporting}
              onClick={handleImportWithOverwrite}
            >
              Import
            </LoadingButton>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default FeaturedOneWayContent;
